/**
 * Adapter Tela 8 Painel de Gestão de P1 — wire (GET /v1/p1/*) → view-models.
 * Funções puras (espelha tela5-adapter).
 *
 * Achados R15 materializados:
 *   - `list` retorna P1Incident CRU, sem relações (DA): card sem cliente/bond/atendente/descrição.
 *   - `escalationChainHistory` é Json append-only (entries {level,score,transitionedAt,reason}) →
 *     a chain do card deriva do PRÓPRIO item da lista, sem N+1 (escalation-chain retorna o mesmo).
 *   - `detectionConfidence` Dec(4,3) serializa string → Number()*100 (D4).
 *   - timeline-mini NÃO tem score por conversa (DB) → dot colore por status, não por risco.
 * Metadados usam os enums REAIS do backend (NÃO os tokens do protótipo).
 * Degradação honesta (padrão Item 317): token desconhecido humaniza; nunca dado falso.
 */
import { relativeTime } from '../../../conversations/format';

import type {
  P1Category,
  P1ChainEntryRaw,
  P1EscalationLevel,
  P1IncidentRaw,
  P1Status,
  P1TimelineMiniResultRaw,
} from '../../types';
import type {
  P1CardVM,
  P1ChainStepVM,
  P1ChainVM,
  P1TimelineNodeVM,
  P1TimelineVM,
  P1Tone,
} from '../../view-models';

const PLACEHOLDER = '—';

/** 6 categorias canônicas (schema P1Category) → label + ícone + tone. */
export const P1_CATEGORY_META: Record<
  P1Category,
  { label: string; icon: string; tone: 'danger' | 'warning' }
> = {
  csam: { label: 'CSAM', icon: '🛡', tone: 'danger' },
  threat: { label: 'Ameaça/violência', icon: '⚠', tone: 'danger' },
  level4_cross_conv: { label: 'Nível 4 multiconversa', icon: '🔄', tone: 'danger' },
  conflict_alert_critical: { label: 'ConflictAlert crítico', icon: '🚨', tone: 'danger' },
  lgpd_critical: { label: 'LGPD crítico', icon: '🔒', tone: 'danger' },
  anonymous_report: { label: 'Denúncia anônima recorr.', icon: '📢', tone: 'warning' },
};

/** 4 níveis canônicos (schema P1EscalationLevel) → label PT + tone do badge. */
export const P1_LEVEL_META: Record<
  P1EscalationLevel,
  { label: string; tone: 'neutral' | 'info' | 'warning' | 'danger' }
> = {
  attendant: { label: 'Atendente', tone: 'neutral' },
  supervisor: { label: 'Supervisor', tone: 'info' },
  manager: { label: 'Gestor', tone: 'warning' },
  director: { label: 'Diretor', tone: 'danger' },
};

export const P1_STATUS_LABELS: Record<P1Status, string> = {
  active: 'Ativo',
  acknowledged: 'Acompanhando',
  handled: 'Em tratamento',
  resolved: 'Resolvido',
  closed: 'Fechado',
};

/** D3 — categoria → slug de runbook canônico seedado. Ausente = sem botão runbook. */
export const RUNBOOK_SLUG_BY_CATEGORY: Partial<Record<P1Category, string>> = {
  csam: 'runbook-csam',
  threat: 'runbook-threat',
  anonymous_report: 'runbook-anonymous-report',
};

export const LEVEL_ORDER: P1EscalationLevel[] = ['attendant', 'supervisor', 'manager', 'director'];

export function nextLevel(level: P1EscalationLevel): P1EscalationLevel | null {
  const i = LEVEL_ORDER.indexOf(level);
  return i >= 0 && i < LEVEL_ORDER.length - 1 ? LEVEL_ORDER[i + 1]! : null;
}

/** D4 — Decimal(4,3) serializa string; coage p/ % inteiro clampado 0-100. */
export function coerceConfidence(v: number | string): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n * 100)));
}

const CHAIN_REASONS: readonly string[] = [
  'auto-escalated',
  'bypassed-offline-attendant',
  'manual-override',
  'detected',
];

/** Type-guard de uma entry de chainHistory (Json não-tipado). Valida todos os
 *  campos que o cast `x is P1ChainEntryRaw` afirma — inclusive `reason` (o backend
 *  sempre o escreve em EscalationChainEntry). */
function isChainEntry(x: unknown): x is P1ChainEntryRaw {
  if (typeof x !== 'object' || x === null) return false;
  const e = x as Record<string, unknown>;
  return (
    typeof e.level === 'string' &&
    e.level in P1_LEVEL_META &&
    typeof e.score === 'number' &&
    typeof e.transitionedAt === 'string' &&
    typeof e.reason === 'string' &&
    CHAIN_REASONS.includes(e.reason)
  );
}

/** D4 — parseia/valida o Json append-only; não-array ou entries inválidas → filtradas. */
export function parseChainHistory(raw: unknown): P1ChainEntryRaw[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isChainEntry);
}

/** Tone do card (.p1-card--*) por categoria + nível. */
export function cardTone(category: P1Category, level: P1EscalationLevel): P1Tone {
  const danger = P1_CATEGORY_META[category].tone === 'danger';
  if (!danger) return 'warning';
  const rank = LEVEL_ORDER.indexOf(level);
  if (rank >= 2) return 'critical-strong'; // manager/director
  if (rank === 1) return 'critical'; // supervisor
  return 'critical-soft'; // attendant
}

function fmtAgo(iso: string): string {
  return relativeTime(iso).replace(/^há /, '');
}

/** Deriva a chain VM do próprio item (sem N+1). History vazia → 1 step current honesto. */
export function adaptChain(item: P1IncidentRaw): P1ChainVM {
  const entries = parseChainHistory(item.escalationChainHistory);
  const next = nextLevel(item.currentEscalationLevel);
  const steps: P1ChainStepVM[] =
    entries.length > 0
      ? entries.map((e) => ({
          level: e.level,
          levelLabel: P1_LEVEL_META[e.level].label,
          tone: P1_LEVEL_META[e.level].tone,
          score: e.score,
          atLabel: fmtAgo(e.transitionedAt),
          current: e.level === item.currentEscalationLevel,
        }))
      : [
          {
            level: item.currentEscalationLevel,
            levelLabel: P1_LEVEL_META[item.currentEscalationLevel].label,
            tone: P1_LEVEL_META[item.currentEscalationLevel].tone,
            score: item.currentScore,
            atLabel: PLACEHOLDER,
            current: true,
          },
        ];
  return {
    currentScore: item.currentScore,
    currentLevel: item.currentEscalationLevel,
    nextLevel: next,
    nextLevelLabel: next !== null ? P1_LEVEL_META[next].label : null,
    steps,
  };
}

function humanizeToken(t: string): string {
  return t.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

const CONV_STATUS_LABELS: Record<string, string> = {
  new: 'Nova',
  active: 'Ativa',
  closed: 'Fechada',
  resolved: 'Resolvida',
  paused: 'Pausada',
};

function isDone(status: string): boolean {
  return status === 'closed' || status === 'resolved';
}

/** DB — timeline sem score: dot por status; nó atual = maior orderIndex. totalDays do range. */
export function adaptTimeline(raw: P1TimelineMiniResultRaw): P1TimelineVM {
  const sorted = [...raw.items].sort((a, b) => a.orderIndex - b.orderIndex);
  const maxIdx = sorted.reduce((m, it) => Math.max(m, it.orderIndex), -Infinity);
  const nodes: P1TimelineNodeVM[] = sorted.map((it) => ({
    convId: it.conversationId,
    atLabel: fmtAgo(it.startedAt),
    statusLabel: CONV_STATUS_LABELS[it.status] ?? humanizeToken(it.status),
    done: isDone(it.status),
    current: it.orderIndex === maxIdx,
  }));
  return {
    sequenceId: raw.sequenceId,
    totalConvs: raw.totalConversations,
    totalDaysLabel: totalDaysLabel(sorted.map((s) => s.startedAt)),
    nodes,
  };
}

function totalDaysLabel(isos: string[]): string {
  if (isos.length === 0) return PLACEHOLDER;
  const times = isos.map((s) => new Date(s).getTime()).filter((t) => Number.isFinite(t));
  if (times.length === 0) return PLACEHOLDER;
  const days = Math.max(0, Math.round((Math.max(...times) - Math.min(...times)) / 86_400_000));
  return `${days}d`;
}

/**
 * D5 — banner "chain divergente": deriva do escalationChainHistory real quando há
 * tanto entry de supervisor quanto de atendente e o score do supervisor está abaixo
 * do score do atendente. Retorna os scores REAIS (sem inventar) ou null.
 */
export function detectChainDivergence(
  item: P1IncidentRaw,
): { supervisorScore: number; attendantScore: number } | null {
  const entries = parseChainHistory(item.escalationChainHistory);
  const sup = entries.filter((e) => e.level === 'supervisor').map((e) => e.score);
  const att = entries.filter((e) => e.level === 'attendant').map((e) => e.score);
  if (sup.length === 0 || att.length === 0) return null;
  const supervisorScore = Math.max(...sup);
  const attendantScore = Math.max(...att);
  return supervisorScore < attendantScore ? { supervisorScore, attendantScore } : null;
}

/** Rótulo PT do runbook por slug canônico (fallback humaniza o slug). */
export function runbookLabel(slug: string): string {
  const map: Record<string, string> = {
    'runbook-csam': 'Runbook CSAM',
    'runbook-threat': 'Runbook de Ameaça',
    'runbook-anonymous-report': 'Runbook de Denúncia Anônima',
    'runbook-incident-massive': 'Runbook de Incidente Massivo',
  };
  return map[slug] ?? humanizeToken(slug.replace(/^runbook-/, '').replace(/-/g, ' '));
}

/** Item cru → card VM. DA: sem cliente/bond/atendente/descrição (omitidos honestos). */
export function adaptCard(item: P1IncidentRaw): P1CardVM {
  const meta = P1_CATEGORY_META[item.category];
  return {
    id: item.id,
    category: item.category,
    categoryLabel: meta.label,
    categoryIcon: meta.icon,
    categoryTone: meta.tone,
    tone: cardTone(item.category, item.currentEscalationLevel),
    conversationId: item.conversationId,
    confidencePct: coerceConfidence(item.detectionConfidence),
    detectedAgoLabel: fmtAgo(item.createdAt),
    createdAt: item.createdAt,
    status: item.status,
    statusLabel: P1_STATUS_LABELS[item.status],
    level: item.currentEscalationLevel,
    engagedByUserId: item.acknowledgedByUserId ?? item.handledByUserId,
    bypassedOffline: item.bypassedAttendantOfflineFlag,
    runbookSlug: RUNBOOK_SLUG_BY_CATEGORY[item.category] ?? null,
    chain: adaptChain(item),
    isLevel4: item.category === 'level4_cross_conv',
  };
}
